import { ConfigProvider, Flex, Typography, theme } from "antd";
import type { GlobalToken } from "antd";
import { useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { pageBleedCss } from "components/layout/pagePadding";
import { Button, libertifyThemeV2 } from "@libertify/ui";
import { colors, spacing, typography } from "@libertify/ui/theme-v2";
import mainBg from "@/assets/images/main-background.png";

const { Title, Text } = Typography;

/**
 * Catch-all 404 for unmatched routes inside the app shell. Its own
 * page — different layout from media-player's not-found page, so
 * nothing is shared with it. The background reuses the existing
 * `main-background.png` asset (same one `BackgroundImage.tsx` already
 * uses for other empty-state screens like `/home` with no projects) —
 * `BackgroundImage` only maps a known, finite list of paths to
 * backgrounds, so a wildcard 404 route needs its own copy of the same
 * image rather than a new entry there.
 */
export const NotFoundPage = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  // AntD's own `sm` breakpoint (used below to explain the media query) —
  // read from the token instead of hardcoding it so this stays correct
  // if AntD ever changes it.
  const { token } = theme.useToken();

  return (
    <ConfigProvider theme={libertifyThemeV2}>
      <StyledCanvas $image={mainBg} $token={token}>
        <StyledWatermark aria-hidden="true">404</StyledWatermark>
        <Flex
          vertical
          align="center"
          gap={spacing.base}
          style={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            // Flex items default to min-width: auto, which lets their
            // content's unwrapped width push past the viewport on
            // narrow screens instead of wrapping — this both caps and
            // unlocks that.
            maxWidth: "min(90vw, 480px)",
            width: "100%",
            minWidth: 0,
          }}
        >
          <Text
            style={{
              fontSize: typography.size.md,
              fontWeight: typography.weight.semibold,
              letterSpacing: typography.letterSpacing.wide,
              textTransform: "uppercase",
              color: colors.accent.active,
            }}
          >
            {t("notFoundPage.eyebrow")}
          </Text>
          <Title
            level={1}
            style={{ margin: 0, fontSize: "clamp(32px, 6vw, 52px)" }}
          >
            {t("notFoundPage.title")}
          </Title>
          <Text
            type="secondary"
            style={{ display: "block", fontSize: 18, maxWidth: 420 }}
          >
            {t("notFoundPage.subtitle")}
          </Text>
          <Button
            variant="primary"
            size="l"
            style={{ marginTop: spacing.xs, fontWeight: 500 }}
            onClick={() => navigate("/home")}
          >
            {t("notFoundPage.backToHome")}
          </Button>
        </Flex>
      </StyledCanvas>
    </ConfigProvider>
  );
};

export default NotFoundPage;

// ─── Styled components ───

const StyledCanvas = styled.div<{ $image: string; $token: GlobalToken }>`
  position: relative;
  min-height: 100vh;

  /* Full-bleed background: cancel the app shell's page padding and apply it
     again inside. */
  ${({ $token }) => pageBleedCss($token, { repad: true })}

  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-image: url(${({ $image }) => $image});
  background-size: cover;
  background-position: top left;
`;

const StyledWatermark = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -58%);
  font-family: Poppins, sans-serif;
  font-weight: 700;
  font-size: clamp(100%, 30vw, 340px);
  line-height: 1;
  letter-spacing: -0.03em;
  color: rgb(222, 240, 239);
  user-select: none;
  pointer-events: none;
  z-index: 0;
`;
